import axios from "axios";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { FRESHNESS } from "./policies";
import { trackReadStarts } from "./reread";
import { ScopeChangedError } from "./scope";

/**
 * The app's one QueryClient (NG-1313).
 *
 * A module singleton rather than something created in a component, so a React
 * Router `clientLoader` can read and prime the cache before the route renders,
 * the same way loaders reach the MobX root store through `getRootStore()`.
 * `root.tsx` hands the same instance to `QueryClientProvider`. Tests get a fresh
 * client each (`render-with-providers.tsx`, `setQueryClientForTesting`).
 *
 * The lifecycle it owns, and nothing else:
 * - **Defaults are the conservative row** of the freshness matrix
 *   (access-sensitive), so a query that forgets its class is re-read once its
 *   60 s are up and on every access hint. Every query definition spreads its
 *   own row from `policies.ts`.
 * - **Query's retry is off**, for queries and mutations. A read's retries are
 *   owned by `runRead` in `policies.ts`; Query's default three would otherwise
 *   stack on top of them.
 * - **Every failure is logged here**, once, at the layer that knows the key.
 */
export function createQueryClient(): QueryClient {
  const conservative = FRESHNESS["access-sensitive"];
  const client = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) =>
        logFailure("query", query.queryKey, error, expectedStatusesOf(query.meta)),
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) =>
        logFailure("mutation", mutation.options.mutationKey, error),
    }),
    defaultOptions: {
      queries: {
        retry: false,
        staleTime: conservative.staleTime,
        gcTime: conservative.gcTime,
        refetchOnMount: conservative.refetchOnMount,
        refetchOnWindowFocus: conservative.refetchOnWindowFocus,
        refetchOnReconnect: conservative.refetchOnReconnect,
      },
      mutations: { retry: false },
    },
  });
  // When each read started, so a re-read can skip what is already newer (`reread.ts`).
  trackReadStarts(client);
  return client;
}

/**
 * HTTP statuses a query expects as an answer rather than a fault, from its
 * `meta.expectedStatuses`: the workspace's project states 403 for a Project
 * Lead, whose picker then falls back to the project's own options.
 */
function expectedStatusesOf(meta: Record<string, unknown> | undefined): readonly number[] {
  const statuses = meta?.expectedStatuses;
  return Array.isArray(statuses) ? statuses.filter((s): s is number => typeof s === "number") : [];
}

/**
 * Logs the key's domain and shape, not its values: a representation can carry
 * user-typed search text, and the tenant/principal prefix adds nothing to act
 * on. A read abandoned by a sign-out is expected, so it only warns, and so is a
 * status the query declares it expects.
 */
function logFailure(
  kind: "query" | "mutation",
  key: readonly unknown[] | undefined,
  error: Error,
  expectedStatuses: readonly number[] = [],
) {
  const domain = key?.[2] ?? "(unkeyed)";
  if (error instanceof ScopeChangedError) {
    console.warn(`[queries] ${kind} ${String(domain)} settled after the session changed`, error);
    return;
  }
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  if (status !== undefined && expectedStatuses.includes(status)) {
    console.warn(`[queries] ${kind} ${String(domain)} answered ${status}, as it may`, error);
    return;
  }
  console.error(`[queries] ${kind} ${String(domain)} failed`, { keyLength: key?.length }, error);
}

let instance: QueryClient | null = null;

/** The app's QueryClient; created on first use, from a loader or from `root.tsx`. */
export function getQueryClient(): QueryClient {
  instance ??= createQueryClient();
  return instance;
}

/** The client if one exists, without creating one: a sign-out must not. */
export function peekQueryClient(): QueryClient | null {
  return instance;
}

/** Point the singleton at a test's own client; `null` restores lazy creation. */
export function setQueryClientForTesting(client: QueryClient | null): void {
  instance = client;
}
